import { useMemo, useState } from 'react';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, X } from 'lucide-react';
import { useToast } from '../../../hooks/useToast';
import { useCVData, type CVSkillGroup } from '../../../lib/cv-mock-data';
import {
  getCanonicalFamilyByKey,
  getCanonicalFamilyLabel,
  getCanonicalSkillsForFamily,
  getSuggestedFamiliesFromProfile,
  inferRoleFamilyFromText,
} from '../../../lib/job-taxonomy';
import { useAISuggestSkills } from '../../../hooks/useOnboardingApi';
import { WebChipList, WebSaveButton, WebAddButton, WebAITag } from './web-form-elements';
import { validateSkillCategory, validateSkillEntry } from './cv-form-validation';
import { confirmDeleteCvItem } from './delete-confirmation';
import { cvChangeNotApplied } from '../../../lib/cv-write-coordinator';

interface EditSkillsFormProps {
  onSave?: () => Promise<void> | void;
  closeOnSave?: boolean;
  ctaLabel?: string;
}

function dedupe(values: string[]) {
  return [...new Set(values.map((value) => value.trim()).filter(Boolean))];
}

// Suggested vs added must never look alike (see EditSkillsForm.tsx): a
// suggestion is an OUTLINED chip with a leading +, an added skill is the filled
// chip WebChipList renders. Both stay on the neutral ramp: the violet
// `--tabbio-button-soft-*` pair these used before made every chip on the screen
// look like the same thing.
const SUGGESTION_CHIP: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  height: 34,
  paddingInline: 14,
  borderRadius: 9999,
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: 'var(--input-border)',
  backgroundColor: 'transparent',
  color: 'var(--text-secondary)',
};

const CHIP_BUTTON: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  border: 'none',
  background: 'transparent',
  color: 'inherit',
  fontFamily: 'inherit',
  fontSize: 14,
  fontWeight: 500,
  cursor: 'pointer',
  padding: 0,
};

const CHIP_DISMISS: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  border: 'none',
  background: 'transparent',
  color: 'var(--text-tertiary)',
  cursor: 'pointer',
  padding: 0,
};

// Keep the in-card suggestion count in step with the native form: past ~8 the
// card stops being scannable and search is the better tool.
const MAX_INLINE_SUGGESTIONS = 8;

const MICRO_LABEL: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: 0.5,
  textTransform: 'uppercase',
  color: 'var(--text-tertiary)',
  fontFamily: 'inherit',
};

function groupMatchesFamily(group: CVSkillGroup, familyKey: string) {
  return inferRoleFamilyFromText([group.category, ...group.items].join(' ')).family?.key === familyKey;
}

export function EditSkillsForm({ onSave, closeOnSave = true, ctaLabel }: EditSkillsFormProps) {
  const { t, i18n } = useTranslation('seeker');
  const { profile, updateProfile } = useCVData();
  const router = useRouter();
  const toast = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const [groups, setGroups] = useState<CVSkillGroup[]>(
    profile.skillGroups.map((group) => ({ ...group, items: [...group.items] })),
  );
  const [pendingInputs, setPendingInputs] = useState<Record<number, string>>({});
  const [dismissedSuggestions, setDismissedSuggestions] = useState<Set<string>>(
    () => new Set(),
  );

  const removeGroupImmediately = (index: number) => {
    setGroups((current) => current.filter((_, currentIndex) => currentIndex !== index));
    // Issue #54: re-index pendingInputs so a removed group's typed-but-unadded
    // skill text doesn't linger at its index and get inherited by the next group
    // added there (which would show an empty title but a prefilled skill).
    setPendingInputs((current) => {
      const next: Record<number, string> = {};
      for (const [key, value] of Object.entries(current)) {
        const i = Number(key);
        if (i === index) continue;
        next[i > index ? i - 1 : i] = value;
      }
      return next;
    });
  };
  const removeGroup = (index: number) => {
    void confirmDeleteCvItem(t, 'skillGroup', () => removeGroupImmediately(index));
  };

  const removeSkill = (groupIndex: number, skillIndex: number) => {
    void confirmDeleteCvItem(t, 'skill', () => {
      setGroups((current) => {
        const updated = [...current];
        updated[groupIndex] = {
          ...updated[groupIndex],
          items: updated[groupIndex].items.filter((_, currentIndex) => currentIndex !== skillIndex),
        };
        return updated;
      });
    });
  };

  const dismissSuggestion = (skill: string) => {
    setDismissedSuggestions((prev) => {
      const next = new Set(prev);
      next.add(skill.toLowerCase());
      return next;
    });
  };

  const suggestedFamilies = useMemo(
    () => getSuggestedFamiliesFromProfile(profile, i18n.language),
    [i18n.language, profile],
  );
  const aiSuggest = useAISuggestSkills();
  const aiLoading = aiSuggest.isPending;

  const addSkillFromSuggestion = (familyKey: string, skill: string) => {
    const family = getCanonicalFamilyByKey(familyKey);
    if (!family) return;
    setGroups((current) => {
      const label = getCanonicalFamilyLabel(family.key, i18n.language);
      const existingIndex = current.findIndex((group) => groupMatchesFamily(group, family.key));
      if (existingIndex >= 0) {
        const updated = [...current];
        updated[existingIndex] = {
          ...updated[existingIndex],
          category: updated[existingIndex].category.trim() || label,
          items: dedupe([...updated[existingIndex].items, skill]),
        };
        return updated;
      }
      return [...current, { category: label, items: [skill] }];
    });
  };

  const addCanonicalSkill = (groupIndex: number, familyKey: string, skill: string) => {
    const family = getCanonicalFamilyByKey(familyKey);
    if (!family) return;

    setGroups((current) => {
      const label = getCanonicalFamilyLabel(family.key, i18n.language);
      const updated = [...current];
      updated[groupIndex] = {
        ...updated[groupIndex],
        category: updated[groupIndex].category.trim() || label,
        items: dedupe([...updated[groupIndex].items, skill]),
      };
      return updated;
    });
  };

  const handleAISuggest = async () => {
    if (aiSuggest.isPending) return;
    const existingSkills = groups.flatMap((g) => g.items);
    try {
      const result = await aiSuggest.mutateAsync({
        title: profile.headline || undefined,
        experience: profile.experience.map((exp) => ({
          title: exp.role,
          company: exp.company,
          description: [exp.overview, ...(exp.bullets || [])]
            .filter(Boolean)
            .join('\n'),
          achievements: exp.bullets || [],
        })),
        education: profile.education.map((edu) => ({
          school: edu.school,
          degree: edu.degree,
        })),
        existingSkills,
      });

      if (!result.categories?.length) {
        toast.error(t('cv.edit.saveFailed'));
        return;
      }

      setGroups((current) => {
        const next = current.map((g) => ({ ...g, items: [...g.items] }));
        for (const cat of result.categories) {
          const incoming = (cat.skills || []).filter(
            (s): s is string => typeof s === 'string' && s.trim().length > 0,
          );
          if (!incoming.length) continue;
          const matchIdx = next.findIndex(
            (g) =>
              g.category.trim().toLowerCase() === cat.name.trim().toLowerCase(),
          );
          if (matchIdx >= 0) {
            next[matchIdx] = {
              ...next[matchIdx],
              items: dedupe([...next[matchIdx].items, ...incoming]),
            };
          } else {
            next.push({ category: cat.name, items: dedupe(incoming) });
          }
        }
        return next;
      });
    } catch {
      toast.error(t('cv.edit.saveFailed'));
    }
  };

  const groupCategoryHasError = groups.some(
    (group) => group.category.trim() && Boolean(validateSkillCategory(group.category, t)),
  );
  const pendingSkillError = groups.some((group, idx) => {
    const v = (pendingInputs[idx] || '').trim();
    return v ? Boolean(validateSkillEntry(v, group.items, t)) : false;
  });

  const handleSave = async () => {
    if (isSaving || groupCategoryHasError || pendingSkillError) return;
    const committedGroups = groups.map((group, idx) => {
      const v = (pendingInputs[idx] || '').trim();
      if (!v) return group;
      const err = validateSkillEntry(v, group.items, t);
      if (err) return group;
      return { ...group, items: dedupe([...group.items, v]) };
    });
    setGroups(committedGroups);
    setPendingInputs({});
    updateProfile({ skillGroups: committedGroups.filter((group) => group.category.trim()) });
    if (onSave) {
      setIsSaving(true);
      try {
        await onSave();
        toast.success(t('cv.edit.saved'));
        if (closeOnSave && router.canGoBack()) router.back();
      } catch (error) {
        // B02: a CV conflict the user answered is not a failure. "Discard"
        // keeps the version saved elsewhere; "keep editing" keeps this draft.
        const outcome = cvChangeNotApplied(error);
        if (outcome) {
          if (outcome === 'discarded' && closeOnSave && router.canGoBack()) router.back();
          return;
        }
        toast.error(t('cv.edit.saveFailed'));
      } finally {
        setIsSaving(false);
      }
    } else {
      router.back();
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-tertiary)' }}>
          {t('cv.edit.suggestedSkills')}
        </span>
        <WebAITag
          label={t('cv.edit.aiSuggestSkills')}
          onClick={handleAISuggest}
          loading={aiLoading}
        />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {(() => {
          const existing = new Set(groups.flatMap((g) => g.items.map((i) => i.toLowerCase())));
          return suggestedFamilies.map((family) => {
            const visible = family.skills.filter(
              (s) => !existing.has(s.toLowerCase()) && !dismissedSuggestions.has(s.toLowerCase()),
            );
            if (!visible.length) return null;
            return (
              <div key={family.key}>
                <div style={{ ...MICRO_LABEL, marginBottom: 8 }}>{family.label}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {visible.map((skill) => (
                    <div key={skill} style={SUGGESTION_CHIP}>
                      <button
                        type="button"
                        onClick={() => addSkillFromSuggestion(family.key, skill)}
                        aria-label={t('cv.edit.addSkillNamed', { skill })}
                        style={CHIP_BUTTON}
                      >
                        <Plus size={12} color="var(--text-tertiary)" strokeWidth={2.5} />
                        {skill}
                      </button>
                      <button
                        type="button"
                        onClick={() => dismissSuggestion(skill)}
                        aria-label={t('cv.edit.dismissSuggestion', { skill })}
                        style={CHIP_DISMISS}
                      >
                        <X size={12} strokeWidth={2} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            );
          });
        })()}
      </div>

      {groups.map((group, groupIndex) => {
        const inferredFamily = inferRoleFamilyFromText([group.category, ...group.items].join(' ')).family;
        const itemLower = new Set(group.items.map((s) => s.toLowerCase()));
        const canonicalSkills = (
          inferredFamily ? getCanonicalSkillsForFamily(inferredFamily.key) : []
        )
          .filter(
            (skill) =>
              !dismissedSuggestions.has(skill.toLowerCase()) &&
              !itemLower.has(skill.toLowerCase()),
          )
          .slice(0, MAX_INLINE_SUGGESTIONS);
        const categoryError = group.category.trim()
          ? validateSkillCategory(group.category, t)
          : undefined;

        return (
          <div
            key={groupIndex}
            style={{
              borderRadius: 20,
              padding: 16,
              // --hover is theme-aware (white overlay in dark); the old
              // rgba(0,0,0,0.04) darkened the card into the near-black page.
              backgroundColor: 'var(--hover)',
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input
                value={group.category}
                onChange={(event) =>
                  setGroups((current) => {
                    const updated = [...current];
                    updated[groupIndex] = { ...updated[groupIndex], category: event.target.value };
                    return updated;
                  })
                }
                placeholder={t('cv.edit.categoryName')}
                dir="auto"
                style={{
                  flex: 1,
                  border: 'none',
                  borderBottom: `1px solid ${categoryError ? 'var(--color-danger, #dc2626)' : 'var(--input-border)'}`,
                  background: 'transparent',
                  color: 'var(--text-primary)',
                  fontFamily: 'inherit',
                  fontSize: 16,
                  fontWeight: 600,
                  outline: 'none',
                  paddingBlock: 4,
                }}
              />
              {/*
                A red × is a decoration, not a warning: the destructive intent is
                carried by the confirm dialog. A quiet trash glyph in a round hit
                target reads as an action instead of an error.
              */}
              <button
                type="button"
                onClick={() => removeGroup(groupIndex)}
                aria-label={t('cv.edit.removeCategory')}
                title={t('cv.edit.removeCategory')}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 32,
                  height: 32,
                  flexShrink: 0,
                  borderRadius: 9999,
                  border: 'none',
                  background: 'transparent',
                  color: 'var(--text-tertiary)',
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                <Trash2 size={16} strokeWidth={1.75} />
              </button>
            </div>
            {categoryError ? (
              <span style={{ color: 'var(--color-danger, #dc2626)', fontSize: 12, paddingInline: 4 }}>
                {categoryError}
              </span>
            ) : null}

            <WebChipList
              items={group.items}
              onAdd={(skill) =>
                setGroups((current) => {
                  const updated = [...current];
                  updated[groupIndex] = {
                    ...updated[groupIndex],
                    items: dedupe([...updated[groupIndex].items, skill]),
                  };
                  return updated;
                })
              }
              onRemove={(skillIndex) => removeSkill(groupIndex, skillIndex)}
              placeholder={t('cv.edit.addSkill')}
              addLabel={t('cv.edit.add')}
              emptyHint={t('cv.edit.addSkillHint')}
              validate={(value) => validateSkillEntry(value, group.items, t)}
              inputValue={pendingInputs[groupIndex] || ''}
              onInputChange={(value) =>
                setPendingInputs((current) => ({ ...current, [groupIndex]: value }))
              }
            />

            {canonicalSkills.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <span style={MICRO_LABEL}>{t('cv.edit.suggestedSkills')}</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {canonicalSkills.map((skill) => (
                    <div key={skill} style={SUGGESTION_CHIP}>
                      <button
                        type="button"
                        onClick={() => addCanonicalSkill(groupIndex, inferredFamily!.key, skill)}
                        aria-label={t('cv.edit.addSkillNamed', { skill })}
                        style={CHIP_BUTTON}
                      >
                        <Plus size={12} color="var(--text-tertiary)" strokeWidth={2.5} />
                        {skill}
                      </button>
                      <button
                        type="button"
                        onClick={() => dismissSuggestion(skill)}
                        aria-label={t('cv.edit.dismissSuggestion', { skill })}
                        style={CHIP_DISMISS}
                      >
                        <X size={12} strokeWidth={2} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        );
      })}

      {groups.length === 0 ? (
        <div
          style={{
            padding: '32px 0',
            textAlign: 'center',
            color: 'var(--text-tertiary)',
            fontSize: 14,
          }}
        >
          {t('cv.edit.noSkillsYet')}
        </div>
      ) : null}

      <WebAddButton label={t('cv.edit.addCategory')} onClick={() => setGroups((current) => [...current, { category: '', items: [] }])} />
      <WebSaveButton label={ctaLabel ?? t('cv.edit.save')} onClick={handleSave} loading={isSaving} disabled={isSaving || groupCategoryHasError || pendingSkillError} />
    </div>
  );
}
